import { faBuilding } from '@fortawesome/free-regular-svg-icons';
import {
  faCheck,
  faEnvelope,
  faMobile,
  faPen,
  faPlus,
  faTimes,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import Avatar from 'components/common/Avatar';
import EmptyList from 'components/common/EmptyList'; // Importe o EmptyList
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import Pagination from 'components/pagination';
import useApi from 'helpers/api';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import usePhotoCache from 'hooks/usePhotoCache';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';
import paths from 'routes/paths';
import AgentPhotoService from 'services/AgentPhoto.service';
import defaultPhoto from '../../assets/img/user-avatar.png';

const AgentRegistration = () => {
  const api = useApi();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const navigate = useNavigate();
  const {
    photos: agentPhotos,
    isLoading: isLoadingPhotos,
    stats: photoStats,
    loadPhotos,
    updatePhoto,
    clearCache: clearPhotoCache
  } = usePhotoCache();

  const [isLoading, setIsLoading] = useState(false);
  const [licenses, setLicenses] = useState({
    userLicenseLimit: 0,
    userLicenseUsed: 0
  });
  const [agents, setAgents] = useState({
    content: [
      {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        inactive: false,
        profile: {},
        photo: ''
      }
    ]
  });
  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    includeInactives: false
  });

  const isEmpty = !isLoading && agents.content.length === 0;

  useEffect(() => {
    getData();
  }, [filters]);

  useEffect(() => {
    viewLicense();
  }, []);

  const getData = async () => {
    setIsLoading(true);
    const queryParams = new URLSearchParams(filters).toString();
    const response = await api.agents.list(queryParams);
    if (response.content) {
      setAgents(response);
      // Carregar fotos dos agentes de forma otimizada
      const agentIds = response.content
        .filter(agent => agent.id)
        .map(agent => agent.id);

      // Função para buscar foto individual
      const fetchPhoto = async agentId => {
        try {
          const response = await AgentPhotoService.find(agentId);
          if (response.ok) {
            const photo = await response.json();
            return photo.url;
          }
          return null;
        } catch (error) {
          console.error(`Erro ao carregar foto do agente ${agentId}:`, error);
          return null;
        }
      };

      loadPhotos(agentIds, fetchPhoto, defaultPhoto);
    }
    setIsLoading(false);
  };

  const viewLicense = async () => {
    const response = await api.agents.viewLicenses();
    response.userLicenseLimit && setLicenses(response);
  };

  const reloadAgentPhoto = async agentId => {
    try {
      const response = await AgentPhotoService.find(agentId);
      if (response.ok) {
        const photo = await response.json();
        updatePhoto(agentId, photo.url);
      } else {
        updatePhoto(agentId, defaultPhoto);
      }
    } catch (error) {
      console.error(`Erro ao recarregar foto do agente ${agentId}:`, error);
      updatePhoto(agentId, defaultPhoto);
    }
  };

  const columns = [
    {
      accessorKey: 'firstName',
      header: 'Nome',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { id, firstName, lastName } = rowData.row.original;
        const photo = agentPhotos[id] || defaultPhoto;
        return (
          <Flex alignItems="center">
            {isLoadingPhotos ? (
              <Skeleton circle width={32} height={32} className="me-2" />
            ) : (
              <Avatar src={photo} className="me-2" size="m" />
            )}
            {firstName} {lastName}
          </Flex>
        );
      }
    },
    {
      accessorKey: 'email',
      header: 'E-mail de acesso',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { email } = rowData.row.original;
        return (
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faEnvelope} size="sm" className="me-2" />
            {email}
          </Flex>
        );
      }
    },
    {
      accessorKey: 'phone',
      header: 'Celular',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { phone } = rowData.row.original;
        return (
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faMobile} size="sm" className="me-2" />
            {phone ? phone : '-'}
          </Flex>
        );
      }
    },
    {
      accessorKey: 'customers',
      header: 'Clientes',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { associatedCustomers, canAccessAllCustomers } =
          rowData.row.original;
        const associatedCustomersNames =
          associatedCustomers &&
          associatedCustomers.map(customer => customer.name).join(', ');
        const viewCustomers = canAccessAllCustomers
          ? 'Todos'
          : associatedCustomersNames;
        return (
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faBuilding} size="sm" className="me-2" />
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{viewCustomers}</Tooltip>}
            >
              <span
                className="text-truncate"
                style={{ maxWidth: '200px', textOverflow: 'ellipsis' }}
              >
                {viewCustomers}
              </span>
            </OverlayTrigger>
          </Flex>
        );
      }
    },
    {
      accessorKey: 'profile',
      header: 'Perfil de permissão',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { profile } = rowData.row.original;
        return (
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faUser} size="sm" className="me-2" />
            {profile.name}
          </Flex>
        );
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { inactive } = rowData.row.original;
        return inactive ? (
          <FontAwesomeIcon icon={faTimes} size="sm" className="text-danger" />
        ) : (
          <FontAwesomeIcon icon={faCheck} size="sm" className="text-success" />
        );
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        return (
          <button
            className="btn btn-link btn-sm"
            onClick={() => handleEdit(item)}
          >
            <FontAwesomeIcon icon={faPen} size="sm" />
          </button>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: agents.content,
    columns
  });

  const handleEdit = item => {
    navigate(paths.agentEdit.replace(':id', item.id));
  };

  const noLicenseAvailable =
    licenses.userLicenseUsed === licenses.userLicenseLimit;

  return (
    <>
      <FalconComponentCard>
        <FalconComponentCard.Header title="Agentes">
          <Flex justifyContent="between" alignItems="center" className="w-100">
            <div className="d-flex align-items-center">
              <Badge
                className="me-2"
                bg={noLicenseAvailable ? 'danger' : 'success'}
              >
                {licenses.userLicenseUsed} / {licenses.userLicenseLimit}{' '}
                Licenças
              </Badge>
            </div>

            <div className="d-flex align-items-center">
              {/* Botão para limpar cache (opcional, para debug) */}
              {/* {process.env.NODE_ENV === 'development' && (
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    clearPhotoCache();
                    showSuccessNotification('Cache de fotos limpo com sucesso');
                  }}
                  className="me-2"
                  title="Limpar cache de fotos"
                >
                  🗑️ Limpar Cache
                </Button>
              )} */}

              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => navigate(paths.agentCreate)}
                disabled={noLicenseAvailable}
              >
                <FontAwesomeIcon icon={faPlus} size="sm" className="me-2" />
                Novo
              </Button>
            </div>
          </Flex>
        </FalconComponentCard.Header>
        <FalconComponentCard.Body>
          <Row className="justify-content-end g-0">
            <Col className="col-auto px-3">
              <Form.Select
                size="sm"
                className="mb-3 rounded-pill"
                onChange={e =>
                  setFilters(prevFilters => ({
                    ...prevFilters,
                    includeInactives: e.target.value
                  }))
                }
              >
                <option value="true">Filtro por status</option>
                <option value="true">Todos</option>
                <option value="false">Somente ativos</option>
              </Form.Select>
            </Col>
          </Row>
          {isLoading ? (
            <Skeleton count={10} height={44} baseColor="#5E6E82" />
          ) : isEmpty ? (
            <div>
              <EmptyList imgSize={400} vhSize={73} />
            </div>
          ) : (
            <>
              <AdvanceTableProvider {...table}>
                <AdvanceTable
                  table
                  headerClassName="text-nowrap align-middle"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    striped: true,
                    className: 'fs-10 mb-0 overflow-hidden'
                  }}
                />
              </AdvanceTableProvider>
              <Pagination
                data={agents}
                filters={filters}
                setFilters={setFilters}
              />
            </>
          )}
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </>
  );
};

export default AgentRegistration;
